/**
 * Controllers for the revamped stock event page (issue #1633).
 *
 * Every call uses a relative axios URL so it goes through the app's existing
 * auth / proxy. The AI endpoints (transcript summary, ask, stats summary) are
 * still being rolled out server-side, so callers must treat 403 / 404 / 5xx as
 * "feature unavailable" rather than a hard error — the components degrade
 * gracefully when these reject.
 */
import axios from "axios";
import dayjs from "dayjs";
import { List } from "@/models/table";
import {
    EventListRow,
    EventListHeaders,
    EarningsFinancials,
    QuarterlyStatistics,
    MixedEventStatistics,
    TranscriptDetail,
    TranscriptAISummary,
    TranscriptAskResponse,
    EventStatsAISummary,
} from "@/models/event-page";

/**
 * Events rail: every event across types for a company over the last few months.
 * Rows carry epoch-ms `date`, `type`, `title`, and `hasTranscripts` (camelCased
 * by the list serializer).
 */
/**
 * Re-throw axios cancellations (so callers can ignore them) but swallow every
 * other error to a caller-supplied safe default. Keeps a failed / 4xx / 5xx /
 * oddly-shaped response from ever throwing into a component.
 */
const safe = async <T>(promise: Promise<T>, fallback: T): Promise<T> => {
    try {
        return await promise;
    } catch (err) {
        if (axios.isCancel(err)) throw err;
        return fallback;
    }
};

export const getCompanyEventsList = async (companyId: number, signal?: AbortSignal): Promise<EventListRow[]> => {
    // Pull several years of history (the API otherwise defaults to ~3 months).
    const fromDate = dayjs().subtract(3, "year").format("YYYY-MM-DD");
    return safe(
        axios
            .get<List<EventListRow, EventListHeaders>>(`/interface/api/events/company/${companyId}/list`, { params: { page_size: 200, page: 1, from_date: fromDate }, signal })
            .then((res) => res.data?.rows ?? []),
        [],
    );
};

/** Per-event EPS / revenue / EBIT / net-income actual-vs-estimate beats. */
export const getCompanyEarningsFinancials = async (companyId: number, signal?: AbortSignal): Promise<EarningsFinancials[]> => {
    return safe(
        axios.get<{ data: EarningsFinancials[] }>(`/interface/api/events/company/${companyId}/financials`, { signal }).then((res) => res.data?.data ?? []),
        [],
    );
};

/** Quarterly earnings statistics table + the population-level aggregates block. */
export const getCompanyQuarterlyStatistics = async (companyId: number, signal?: AbortSignal): Promise<QuarterlyStatistics> => {
    return safe(
        axios.get<QuarterlyStatistics>(`/interface/api/events/company/${companyId}/statistics/quarterly`, { signal }).then((res) => res.data ?? {}),
        {},
    );
};

/** Mixed (non-earnings) per-event price reaction statistics. */
export const getCompanyMixedEventStatistics = async (companyId: number, signal?: AbortSignal): Promise<MixedEventStatistics> => {
    return safe(
        axios.get<MixedEventStatistics>(`/interface/api/events/company/${companyId}/statistics`, { signal }).then((res) => res.data ?? { events: [] }),
        { events: [] },
    );
};

// ?external_id= disambiguates by CIQ keydevid so the right event resolves across event
// models whose per-table PKs collide (the unified list mixes earnings + conference). The
// backend falls back to the path PK when it is absent, keeping the legacy page working.
const extQuery = (externalId?: number) => (externalId != null ? `?external_id=${externalId}` : "");

/** Full transcript (participants + components) for an event. */
export const getEventTranscript = async (eventId: number, externalId?: number, signal?: AbortSignal): Promise<TranscriptDetail> => {
    return safe(
        axios.get<TranscriptDetail>(`/interface/api/events/${eventId}/get_transcript${extQuery(externalId)}`, { signal }).then((res) => res.data ?? {}),
        {},
    );
};

/** AI-generated summary of the call transcript (may 404 while rolling out). */
export const getTranscriptAISummary = async (eventId: number, externalId?: number, signal?: AbortSignal): Promise<TranscriptAISummary | null> => {
    return safe(
        axios.get<TranscriptAISummary>(`/interface/api/events/${eventId}/transcript/summary${extQuery(externalId)}`, { signal }).then((res) => res.data ?? null),
        null,
    );
};

/**
 * Ask a question against a transcript. Gated — a 403 means the caller's plan
 * doesn't include the assistant, which the UI surfaces as an upsell. The 403
 * (and other errors) must propagate here so the component can distinguish the
 * upsell case; the component already catches.
 */
export const askTranscript = async (eventId: number, question: string, externalId?: number, signal?: AbortSignal): Promise<TranscriptAskResponse> => {
    const res = await axios.post<TranscriptAskResponse>(`/interface/api/events/${eventId}/transcript/ask${extQuery(externalId)}`, { question }, { signal });
    return res.data ?? {};
};

/** AI-generated narrative summary of the event statistics. */
export const getEventStatsAISummary = async (companyId: number, signal?: AbortSignal): Promise<EventStatsAISummary | null> => {
    return safe(
        axios.get<EventStatsAISummary>(`/interface/api/events/company/${companyId}/statistics/summary`, { signal }).then((res) => res.data ?? null),
        null,
    );
};
